import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import StatusBadge from '../../common/StatusBadge';
import OrderShortedLinesTable from '../../blocks/OrderShortedLinesTable';
import OrderFulfillmentsTable from '../../blocks/OrderFulfillmentsTable';
import OrderActivitiesLogTable from '../../blocks/OrderActivitiesLogTable';
import ConfirmModal from '../ConfirmDeleteModal';
import { convertedCurrencyFormat, datetimeFormatter, orderTypeFormatter } from '../../../utils/formatHelper';
import { getFullAddressInHtml, getShippingMethod } from '../../../utils/orderHelper';
import { fullName } from '../../../utils/textHelper';
import {
  FULFILLMENT_STATE_WAITING_FOR_SHIPPING,
  FULFILLMENT_STATE_CSR,
  RETAILER_STATE_ORDERED,
} from '../../../constants/orderStates';
import AutoCancelledOrderLinesTable from '../../blocks/AutoCancelledOrderLinesTable';
import { OrderRlrsTable } from '../../blocks/OrderRlrsTable/OrderRlrsTable';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { showRlrTableInSupplierOrder } from '../../../constants/featureFlag';
import { CymbioSpinner } from '../../common/Spinner';
import { cancelFulfillments } from '../../../store/actions/brandOrders';

import styles from './SupplierOrderSummaryModal.module.scss';

const NOT_AVAILABLE = 'N/A';
function renderAddress(address) {
  const fullAddress = getFullAddressInHtml(address);
  return (
    <dl>
      <dt>Name:</dt>
      <dd>{fullName(address)}</dd>

      {fullAddress && (
        <>
          <dt>Address:</dt>
          <dd>{fullAddress}</dd>
        </>
      )}
    </dl>
  );
}

function SupplierOrderSummaryModal(props) {
  const dispatch = useDispatch();
  const { loading, onClose, order, additionalStates, opened = false } = props;
  const showRlrsTable = useFeatureFlag(showRlrTableInSupplierOrder, false, true);
  const [confirmCancel, setConfirmCancel] = useState({ open: false });

  if (!order || loading) {
    return (
      <ModalWindow open={opened} onClose={onClose} size="xlarge">
        <div className={styles.loading}>
          <CymbioSpinner />
        </div>
      </ModalWindow>
    );
  }

  const {
    created_at,
    currency,
    retailer_order_id,
    type,
    items_total_value,
    items_total_quantity,
    items_total_tax,
    shipping_total_value,
    shipping_total_tax,
    store,
    original_created_at,
    conversion_rate,
    sales_order_number,
    order_exchange_rates,
  } = order;

  const openConfirmOrderModal = () => {
    setConfirmCancel({
      open: true,
      text: `Are you sure you want to cancel order #${order.retailer_order_id}?`,
      fulfillmentIds: order.fulfillments.map((fulfillment) => fulfillment.id),
    });
  };

  const openConfirmFulfillmentModal = (fulfillmentId) => {
    setConfirmCancel({
      open: true,
      text: `Are you sure you want to cancel fulfillment #${fulfillmentId}?`,
      fulfillmentIds: [fulfillmentId],
    });
  };

  const doCancelFulfillments = () => {
    dispatch(cancelFulfillments(confirmCancel.fulfillmentIds));
    setConfirmCancel({ open: false });

    if (order.fulfillments.length === confirmCancel.fulfillmentIds.length) {
      onClose();
    } else {
      props.onRefresh();
    }
  };

  const hasReturned = order.returned_items_total_quantity > 0;
  const totalValue = hasReturned ? items_total_value - order.returned_items_total_value : items_total_value;
  const totalTax = hasReturned ? items_total_tax - order.returned_items_total_tax : items_total_tax;
  const totalQuantity = hasReturned ? items_total_quantity - order.returned_items_total_quantity : items_total_quantity;
  const documentsLink = order.notes.find((note) => note.name === 'Documents Link');

  const canCancelFulfillments = order.fulfillments.filter((fulfillment) => {
    if (order.expired || order.current_retailer_state.state !== RETAILER_STATE_ORDERED) {
      return false;
    }
    const state = fulfillment.currentState.state;
    return state === FULFILLMENT_STATE_WAITING_FOR_SHIPPING || state === FULFILLMENT_STATE_CSR;
  });
  const canCancelEntireOrder = canCancelFulfillments.length === order.fulfillments.length;

  const hasPackingSlips = order.fulfillments.filter((fulfillment) => fulfillment.packing_slip_link);
  const singlePackingSlip = order.fulfillments.length === 1 && hasPackingSlips.length === 1;
  const isAutoCancelledOrder = order?.fulfillments?.length === 0;
  const fulfillmentIds = order.fulfillments.map((fulfillment) => fulfillment.id).join(', ');

  return (
    <>
      <ModalWindow open={opened} onClose={onClose}>
        <h2 className={styles.title}>
          Order Summary <span className={styles.retailerOrderId}>{retailer_order_id}</span>
        </h2>
        <hr />
        <div className={styles.details}>
          <div className={styles.infoSession}>
            <h3>Order Info</h3>
            <div className={styles.info}>
              <dl>
                <dt>ID:</dt>
                <dd>{retailer_order_id}</dd>

                {sales_order_number && (
                  <>
                    <dt>Sales Order Number:</dt>
                    <dd>{sales_order_number}</dd>
                  </>
                )}

                <dt>Cymbio ID:</dt>
                <dd>{fulfillmentIds}</dd>

                {original_created_at && (
                  <>
                    <dt>Order placed at:</dt>
                    <dd>{datetimeFormatter(original_created_at)}</dd>
                  </>
                )}

                <dt>Order captured at:</dt>
                <dd>{datetimeFormatter(created_at)}</dd>

                <dt>Shipment status:</dt>
                <dd>
                  <StatusBadge
                    status={additionalStates.displayStatus}
                    id={`order_summary_state_${order.id}`}
                    simplify
                  />
                </dd>
                {additionalStates.infoStatus?.length > 0 && (
                  <>
                    <dt>Info:</dt>
                    <dd>
                      <StatusBadge
                        status={additionalStates.infoStatus}
                        id={`order_summary_info_state_${order.id}`}
                        simplify
                        additionalInfo
                      />
                    </dd>
                  </>
                )}
                {additionalStates.postShipmentStatus && (
                  <>
                    <dt>Post-Shipment:</dt>
                    <dd>
                      <StatusBadge
                        status={additionalStates.postShipmentStatus}
                        id={`order_summary_post_shipment_state_${order.id}`}
                        simplify
                        postShipmentStatus
                      />
                    </dd>
                  </>
                )}
                <dt>Type:</dt>
                <dd>{orderTypeFormatter(type)}</dd>
              </dl>
              <dl>
                <dt>Total Order Value:</dt>
                <dd>
                  {!isAutoCancelledOrder
                    ? convertedCurrencyFormat(totalValue, currency, order_exchange_rates)
                    : NOT_AVAILABLE}
                </dd>

                <dt>Tax:</dt>
                <dd>
                  {!isAutoCancelledOrder
                    ? convertedCurrencyFormat(totalTax, currency, conversion_rate, order_exchange_rates)
                    : NOT_AVAILABLE}
                </dd>

                <dt>Number of Units / Cases:</dt>
                <dd>{totalQuantity || NOT_AVAILABLE}</dd>

                <dt>Shipping:</dt>
                <dd>
                  {!isAutoCancelledOrder
                    ? convertedCurrencyFormat(shipping_total_value, currency, conversion_rate, order_exchange_rates)
                    : NOT_AVAILABLE}
                </dd>

                <dt>Shipping Tax:</dt>
                <dd>
                  {!isAutoCancelledOrder
                    ? convertedCurrencyFormat(shipping_total_tax, currency, conversion_rate, order_exchange_rates)
                    : NOT_AVAILABLE}
                </dd>

                <dt>Shipping Method:</dt>
                <dd>{getShippingMethod(order)}</dd>

                {store && (
                  <>
                    <dt>Store:</dt>
                    <dd>{store.name}</dd>
                  </>
                )}
              </dl>
            </div>
          </div>

          <div className={styles.addressSection}>
            <div>
              <h3>Shipping Info</h3>
              {renderAddress(order.shipping_address)}
            </div>

            {order.billing_address?.first_name && (
              <div>
                <h3>Billing Info</h3>
                {renderAddress(order.billing_address)}
              </div>
            )}
          </div>
        </div>
        <div className={styles.actionsRow}>
          {canCancelEntireOrder && !isAutoCancelledOrder && (
            <Button
              color="white"
              text="Cancel Order"
              onClick={() => openConfirmOrderModal()}
              className={styles.actionButton}
            />
          )}
          {documentsLink && (
            <a
              href={documentsLink.value}
              target="_blank"
              className={cn('link-button', styles.actionButton)}
              rel="noopener noreferrer"
            >
              <Button text="Download Documents" />
            </a>
          )}
          {singlePackingSlip && (
            <a
              href={hasPackingSlips[0].packing_slip_link}
              target="_blank"
              className={cn('link-button', styles.actionButton)}
              rel="noopener noreferrer"
            >
              <Button text="Download Packing Slip" />
            </a>
          )}
        </div>

        {isAutoCancelledOrder ? (
          <AutoCancelledOrderLinesTable order={order} />
        ) : (
          <>
            <OrderShortedLinesTable order={order} />
            <OrderFulfillmentsTable order={order} onCancel={openConfirmFulfillmentModal} />
          </>
        )}

        {showRlrsTable && <OrderRlrsTable order={order} />}

        <OrderActivitiesLogTable order={order} />

        <div className={styles.footer}>
          <Button text="Close" onClick={onClose} />
        </div>
      </ModalWindow>

      <ConfirmModal
        open={confirmCancel.open}
        text={confirmCancel.text}
        confirmButton="Yes"
        cancelButton="No"
        onConfirm={doCancelFulfillments}
        onCancel={() => setConfirmCancel({ open: false })}
      />
    </>
  );
}

export default SupplierOrderSummaryModal;

SupplierOrderSummaryModal.propTypes = {
  opened: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.any,
};
